<script setup>
import {reactive, ref} from 'vue';

const emit = defineEmits(['created']);

const workingState = ref('');
const isSubmitting = ref(false);
const form = reactive({
    name: '',
    org_nr: '',
    invoice_email: '',
    employees: '',
    billing_street_address: '',
    billing_city: '',
    billing_zipcode: '',
    billing_country: '',
});
const errors = reactive({
    name: '',
    org_nr: '',
    invoice_email: '',
    employees: '',
    billing_street_address: '',
    billing_city: '',
    billing_zipcode: '',
    billing_country: '',
});

const resetErrors = () => {
    for (const key of Object.keys(errors)) {
        errors[key] = '';
    }
};

const submit = async () => {
    if (isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;
    workingState.value = 'working';
    resetErrors();

    try {
        const res = await axios.post(route('bo.api.v1.crm.lead-company.store'), {
            ...form,
            employees: form.employees === '' || form.employees === null
                ? null
                : Number(form.employees),
        });

        workingState.value = 'success';
        emit('created', res.data.lead_company);
    } catch (error) {
        workingState.value = 'error';

        const validationErrors = error.response?.data?.errors ?? {};

        for (const key of Object.keys(errors)) {
            errors[key] = validationErrors[key]?.[0] ?? '';
        }
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<template>
    <div class="@container">
        <div>
            <input-text
                v-model:content="form.name"
                maxlength="255"
                input-name="lead_company_name"
                input-id="lead_company_name"
                :label="_mft('crm:contact.company.fields.name')"
                :error="errors.name"
            />

            <input-text
                v-model:content="form.org_nr"
                maxlength="255"
                input-name="lead_company_org_nr"
                input-id="lead_company_org_nr"
                :label="_mft('crm:contact.company.fields.corporateIdentityNumber')"
                :error="errors.org_nr"
            />

            <input-text
                v-model:content="form.invoice_email"
                maxlength="255"
                input-name="lead_company_invoice_email"
                input-id="lead_company_invoice_email"
                :label="_mft('crm:contact.company.fields.invoiceEmail')"
                :error="errors.invoice_email"
            />

            <input-number
                v-model:content="form.employees"
                input-name="lead_company_employees"
                input-id="lead_company_employees"
                :label="_mft('crm:contact.company.fields.employees')"
                :error="errors.employees"
            />

            <input-text
                v-model:content="form.billing_street_address"
                maxlength="255"
                input-name="lead_company_billing_street_address"
                input-id="lead_company_billing_street_address"
                :label="_mft('crm:contact.company.fields.billingStreetAddress')"
                :error="errors.billing_street_address"
            />

            <input-text
                v-model:content="form.billing_zipcode"
                maxlength="255"
                input-name="lead_company_billing_zipcode"
                input-id="lead_company_billing_zipcode"
                :label="_mft('crm:contact.company.fields.billingZipcode')"
                :error="errors.billing_zipcode"
            />

            <input-text
                v-model:content="form.billing_city"
                maxlength="255"
                input-name="lead_company_billing_city"
                input-id="lead_company_billing_city"
                :label="_mft('crm:contact.company.fields.billingCity')"
                :error="errors.billing_city"
            />

            <input-language
                :label="_mft('crm:contact.company.fields.billingCountry')"
                name="lead_company_country"
                v-model:country="form.billing_country"
                @selected="form.billing_country = $event.label"
            />

            <input-error
                v-if="errors.billing_country"
                :error="errors.billing_country"
            />
        </div>

        <div class="mt-6 flex justify-end">
            <button-success
                type="button"
                :state="workingState"
                :disabled="isSubmitting"
                @click="submit"
            >
                {{ _mft('crm:leadCompany.createCompany') }}
            </button-success>
        </div>
    </div>
</template>
